import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ModalController, MODAL_CONTROLLER, MODAL_DATA } from 'shared';
import { Observable, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { EquipmentMetaInfo } from '../../../services/installation-configuration.service';
import { UserTrackingHelperService } from '../../../services/user-tracking-helper.service';
import { ModalComponent } from '../../../../../../shared/src/lib/components/modal/modal.component';
import { ModalContentComponent } from '../../../../../../shared/src/lib/components/modal-content/modal-content.component';
import { ModalSectionComponent } from '../../../../../../shared/src/lib/components/modal-section/modal-section.component';
import { ModalSectionColumnComponent } from '../../../../../../shared/src/lib/components/modal-section-column/modal-section-column.component';
import { InputLabelComponent } from '../../../../../../shared/src/lib/components/input-label/input-label.component';
import { ToggleComponent } from '../../../../../../shared/src/lib/components/toggle/toggle.component';
import { InputFieldComponent } from '../../../../../../shared/src/lib/components/input-field/input-field.component';
import { ModalButtonsComponent } from '../../../../../../shared/src/lib/components/modal-buttons/modal-buttons.component';
import { ModalButtonComponent } from '../../../../../../shared/src/lib/components/modal-button/modal-button.component';
import { Angulartics2OnModule } from 'angulartics2';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe } from '@angular/common';

export interface MixitUnderfloorProtectionResponse {
  enabled: boolean;
  maxTemperature: number;
}

export interface MixitUnderfloorProtectionSettings {
  enabled: boolean;
  maxTemperature: number | null;
  maxTemperatureMeta: EquipmentMetaInfo;
}

@Component({
    selector: 'app-mixit-underfloor-protection-modal',
    templateUrl: './mixit-underfloor-protection-modal.component.html',
    styleUrls: ['./mixit-underfloor-protection-modal.component.scss'],
    imports: [
        ModalComponent,
        ModalContentComponent,
        FormsModule,
        ReactiveFormsModule,
        ModalSectionComponent,
        ModalSectionColumnComponent,
        InputLabelComponent,
        ToggleComponent,
        InputFieldComponent,
        ModalButtonsComponent,
        ModalButtonComponent,
        Angulartics2OnModule,
        TranslateModule,
        AsyncPipe,
    ]
})
export class MixitUnderfloorProtectionModalComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  public valid$: Observable<boolean>;

  public maxTemperatureMinMax: { min: number; max: number };

  public form: UntypedFormGroup;

  constructor(
    @Inject(MODAL_DATA) public data: MixitUnderfloorProtectionSettings,
    @Inject(MODAL_CONTROLLER) private controller: ModalController<MixitUnderfloorProtectionResponse>,
    private userTrackingHelperService: UserTrackingHelperService
  ) {}

  ngOnInit(): void {
    this.maxTemperatureMinMax = {
      min: this.data.maxTemperatureMeta.min,
      max: this.data.maxTemperatureMeta.max,
    };

    this.form = new UntypedFormGroup({
      enabled: new UntypedFormControl(),
      maxTemperature: new UntypedFormControl('', [
        Validators.required,
        Validators.min(this.data.maxTemperatureMeta.min),
        Validators.max(this.data.maxTemperatureMeta.max),
      ]),
    });

    this.valid$ = this.form.valueChanges.pipe(
      map((form) => {
        if (form.enabled) {
          return this.form.controls.maxTemperature.valid;
        }
        return true;
      })
    );

    this.subscription.add(
      this.form.controls.enabled.valueChanges
        .pipe(
          tap((enabled) => {
            this.toggleEnabled(enabled);
          })
        )
        .subscribe()
    );

    this.toggleEnabled(this.data.enabled);

    setTimeout(() => {
      this.form.setValue({
        enabled: this.data.enabled,
        maxTemperature: this.data.maxTemperature,
      });

      this.subscription.add(this.userTrackingHelperService.startFormValueChangeTracking('adjustUnderfloorProtectionShown', this.form));
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  close() {
    this.controller.dismiss();
  }

  save() {
    this.controller.complete({
      enabled: this.form.controls.enabled.value,
      maxTemperature: this.form.controls.maxTemperature.value,
    });
  }

  private toggleEnabled(enabled: any) {
    if (enabled) {
      this.form.controls.maxTemperature.enable();
    } else {
      this.form.controls.maxTemperature.disable();
    }
  }

  dismiss() {
    this.userTrackingHelperService.trackUserAction('adjustUnderfloorProtectionShown', 'exitClicked');
  }
}
